// FilterBar — 타입·태그·정렬 복합 필터 컴포넌트.
// 2026-10-03: 즐겨찾기 체크박스·"최근 사용순"·필터 초기화 제거 (상단 즐겨찾기/최근 구역과 중복).
//   기본 정렬 = 이름순 (utils/explorerView DEFAULT_SORT).
// URL 쿼리스트링이 SoT. 모든 필터 변경은 useSearchParams를 통해 URL을 갱신한다.
// 단일 type 필터 정책 (설계 §3): type=하나만 선택 가능 (재클릭 시 해제).

import { useSearchParams } from 'react-router-dom';
import { useTags } from '../hooks/useTags';
import type { ItemSortKey, ItemType } from '../api/items';
import { DEFAULT_SORT } from '../utils/explorerView';

// ─── 타입 정의 ─────────────────────────────────────────

interface TypeOption {
  value: ItemType;
  label: string;
  icon: string;
}

interface SortOption {
  value: ItemSortKey;
  label: string;
}

// ─── 상수 ──────────────────────────────────────────────

const TYPE_OPTIONS: TypeOption[] = [
  { value: 'credential_account', label: '계정', icon: '👤' },
  { value: 'credential_api_key', label: 'API 키', icon: '🔑' },
  { value: 'credential_server', label: '서버', icon: '🖥️' },
  { value: 'credential_env', label: '.env', icon: '📋' },
  { value: 'credential_auth', label: '인증', icon: '🛡️' },
  { value: 'note', label: '메모', icon: '📝' },
  { value: 'server', label: '서버 인프라', icon: '🗄️' },
  { value: 'domain', label: '도메인', icon: '🌐' },
  { value: 'service', label: '서비스', icon: '⚙️' },
  { value: 'subscription', label: '구독', icon: '💳' },
  { value: 'file', label: '파일', icon: '📁' },
  { value: 'checklist', label: '체크리스트', icon: '✅' },
];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name', label: '이름순' },
  { value: 'created_at', label: '생성일순' },
  { value: 'expires_soon', label: '만료 임박순' },
  { value: 'use_count', label: '사용 빈도순' },
];

// ─── FilterBar 컴포넌트 ────────────────────────────────

export function FilterBar() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentType = searchParams.get('type') as ItemType | null;
  const currentTag = searchParams.get('tag') ?? '';
  const sortParam = searchParams.get('sort') as ItemSortKey | null;
  // 목록에 없는 값(예: 예전 URL의 last_used_at)은 기본값으로 표시
  const currentSort: ItemSortKey =
    sortParam && SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : DEFAULT_SORT;

  // 태그 자동완성 — 전체 태그 목록
  const { data: allTags = [] } = useTags('', 50);

  // ─── 핸들러 ──────────────────────────────────────────

  // 타입 선택 토글 (단일 선택, 재클릭 시 해제)
  const handleTypeToggle = (type: ItemType) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (currentType === type) {
        next.delete('type');
      } else {
        next.set('type', type);
      }
      next.delete('offset');
      return next;
    }, { replace: true });
  };

  // 태그 선택 토글
  const handleTagToggle = (tagName: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (currentTag === tagName) {
        next.delete('tag');
      } else {
        next.set('tag', tagName);
      }
      next.delete('offset');
      return next;
    }, { replace: true });
  };

  // 정렬 변경
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as ItemSortKey;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === DEFAULT_SORT) {
        next.delete('sort'); // 기본값이면 제거
      } else {
        next.set('sort', val);
      }
      next.delete('offset');
      return next;
    }, { replace: true });
  };

  return (
    <div className="filter-bar">
      {/* 타입 칩 */}
      <div className="filter-bar__section">
        <span className="filter-bar__label">타입</span>
        <div className="filter-chips" role="group" aria-label="타입 필터">
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip${currentType === opt.value ? ' active' : ''}`}
              onClick={() => handleTypeToggle(opt.value)}
              aria-pressed={currentType === opt.value}
              title={opt.label}
            >
              <span aria-hidden="true">{opt.icon}</span>
              <span className="filter-chip__label">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 태그 칩 (빈도순 상위 태그만 표시) */}
      {allTags.length > 0 && (
        <div className="filter-bar__section">
          <span className="filter-bar__label">태그</span>
          <div className="filter-chips" role="group" aria-label="태그 필터">
            {allTags.slice(0, 15).map((tag) => (
              <button
                key={tag.id}
                type="button"
                className={`filter-chip filter-chip--tag${currentTag === tag.name ? ' active' : ''}`}
                onClick={() => handleTagToggle(tag.name)}
                aria-pressed={currentTag === tag.name}
                style={tag.color ? { '--tag-color': tag.color } as React.CSSProperties : undefined}
              >
                {tag.name}
                <span className="filter-chip__count">{tag.item_count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 정렬 */}
      <div className="filter-bar__section filter-bar__section--row">

        {/* 정렬 셀렉트 */}
        <div className="filter-bar__sort">
          <label htmlFor="explorer-sort" className="filter-bar__label">정렬</label>
          <select
            id="explorer-sort"
            value={currentSort}
            onChange={handleSortChange}
            className="select"
            aria-label="정렬 기준 선택"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

      </div>
    </div>
  );
}
